import { AllocationMedium } from 'api/resource-management/types';
import { DemandRoleSummary } from 'components/organisms/resource-management/common-types/demand';
import {
  SchedulerEventType,
  TeamTotalAllocationEvent,
  TeamTotalHardAllocationEvent,
  TeamTotalSoftAllocationEvent,
  TotalAllocationEvent,
  UserTotalAllocationEvent,
  UserTotalHardAllocationEvent,
  UserTotalSoftAllocationEvent,
} from 'components/organisms/resource-management/event-types';
import { SchedulerResourceType } from 'components/organisms/resource-management/resource-types';
import { getTotalAllocatedMinutes } from '../../common';
import { resourceIdForDemandResource } from '../../id';
import { getSchedulerEventProps, getSchedulerEventsClass } from '../common';
import { idForDemandEvent } from './common';

const getClassNameForTotalAllocationEvent = ({
  usersAllocatedMinutes,
  teamsAllocatedMinutes,
  capacity,
}: DemandRoleSummary): string => {
  const totalMinutes =
    getTotalAllocatedMinutes(usersAllocatedMinutes) +
    getTotalAllocatedMinutes(teamsAllocatedMinutes);

  if (totalMinutes > capacity) {
    return 'over-allocated';
  }

  return '';
};

const getPercentangeFromTotalMinutes = (
  { usersAllocatedMinutes, teamsAllocatedMinutes }: DemandRoleSummary,
  against: number,
) =>
  Math.round(
    (against /
      (getTotalAllocatedMinutes(usersAllocatedMinutes) +
        getTotalAllocatedMinutes(teamsAllocatedMinutes))) *
      100,
  );

export const asTotalAllocationEvent = (
  roleSummary: DemandRoleSummary,
): TotalAllocationEvent => ({
  type: SchedulerEventType.TotalAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.TotalAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.TotalAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  capacity: roleSummary.capacity,
  usersAllocatedMinutes: roleSummary.usersAllocatedMinutes,
  teamsAllocatedMinutes: roleSummary.teamsAllocatedMinutes,
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.TotalAllocation, {
    cls: getSchedulerEventsClass(
      SchedulerEventType.TotalAllocation,
      getClassNameForTotalAllocationEvent(roleSummary),
    ),
  }),
});
export const asUserTotalAllocationEvent = (
  roleSummary: DemandRoleSummary,
): UserTotalAllocationEvent => ({
  type: SchedulerEventType.UserTotalAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.UserTotalAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.UserTotalAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    getTotalAllocatedMinutes(roleSummary.usersAllocatedMinutes),
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: getTotalAllocatedMinutes(roleSummary.usersAllocatedMinutes),
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.UserTotalAllocation),
});
export const asUserTotalSoftAllocationEvent = (
  roleSummary: DemandRoleSummary,
): UserTotalSoftAllocationEvent => ({
  type: SchedulerEventType.UserTotalSoftAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.UserTotalSoftAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.UserTotalSoftAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    roleSummary.usersAllocatedMinutes[AllocationMedium.Soft],
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: roleSummary.usersAllocatedMinutes[AllocationMedium.Soft],
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.UserTotalSoftAllocation),
});
export const asUserTotalHardAllocationEvent = (
  roleSummary: DemandRoleSummary,
): UserTotalHardAllocationEvent => ({
  type: SchedulerEventType.UserTotalHardAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.UserTotalHardAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.UserTotalHardAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    roleSummary.usersAllocatedMinutes[AllocationMedium.Hard],
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: roleSummary.usersAllocatedMinutes[AllocationMedium.Hard],
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.UserTotalHardAllocation),
});
export const asTeamTotalAllocationEvent = (
  roleSummary: DemandRoleSummary,
): TeamTotalAllocationEvent => ({
  type: SchedulerEventType.TeamTotalAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.TeamTotalAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.TeamTotalAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    getTotalAllocatedMinutes(roleSummary.teamsAllocatedMinutes),
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: getTotalAllocatedMinutes(roleSummary.teamsAllocatedMinutes),
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.TeamTotalAllocation),
});
export const asTeamTotalSoftAllocationEvent = (
  roleSummary: DemandRoleSummary,
): TeamTotalSoftAllocationEvent => ({
  type: SchedulerEventType.TeamTotalSoftAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.TeamTotalSoftAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.TeamTotalSoftAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    roleSummary.teamsAllocatedMinutes[AllocationMedium.Soft],
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: roleSummary.teamsAllocatedMinutes[AllocationMedium.Soft],
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.TeamTotalSoftAllocation),
});
export const asTeamTotalHardAllocationEvent = (
  roleSummary: DemandRoleSummary,
): TeamTotalHardAllocationEvent => ({
  type: SchedulerEventType.TeamTotalHardAllocation,
  resourceId: resourceIdForDemandResource(
    roleSummary.roleId,
    SchedulerResourceType.TeamTotalHardAllocation,
  ),
  id: idForDemandEvent(
    SchedulerEventType.TeamTotalHardAllocation,
    roleSummary.roleId,
    roleSummary.startDate,
    roleSummary.forViewPreset,
  ),
  minutesPercentage: getPercentangeFromTotalMinutes(
    roleSummary,
    roleSummary.teamsAllocatedMinutes[AllocationMedium.Hard],
  ),
  startDate: roleSummary.startDate.toDate(),
  endDate: roleSummary.endDate.toDate(),
  roleId: roleSummary.roleId,
  allocatedMinutes: roleSummary.teamsAllocatedMinutes[AllocationMedium.Hard],
  forViewPreset: roleSummary.forViewPreset,
  ...getSchedulerEventProps(SchedulerEventType.TeamTotalHardAllocation),
});
